'use client'

import { Button } from '@/base_components/ui/button'
import { ButtonVariants } from '@/base_components/ui/button/variants'
import { Calendar, CalendarProps } from '@/base_components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/base_components/ui/popover'
import { cn } from '@/lib/utils'
import { isEqual, isToday } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cloneElement, memo, useEffect, useState } from 'react'
import { Matcher } from 'react-day-picker'

type DatePickerProps = {
  selectedDate?: Date
  onDateChange?: (date: Date) => void
  triggerButton: React.ReactElement
  container?: Element | DocumentFragment
  disabled?: Matcher | Matcher[]
  modal?: boolean
  calendarProps?: CalendarProps
  autoApply?: boolean
  footerContent?: (handleSelectDate: (date: Date) => void) => React.ReactNode
}

const DatePicker: React.FC<DatePickerProps> = ({
  selectedDate = new Date(),
  onDateChange,
  triggerButton,
  container,
  disabled,
  modal = false,
  calendarProps = {},
  autoApply = false,
  footerContent,
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const [date, setDate] = useState<Date | undefined>(
    isToday(selectedDate) ? undefined : selectedDate
  )
  const [month, setMonth] = useState<Date>(selectedDate || new Date())

  useEffect(() => {
    if (open && selectedDate) {
      setMonth(selectedDate)
    }
  }, [open, selectedDate])

  useEffect(() => {
    if (date) {
      setDate(isToday(selectedDate) ? undefined : selectedDate)
    }
  }, [selectedDate?.toISOString()])

  const handleApplyDate = (changedDate: Date | undefined) => {
    onDateChange?.(changedDate || selectedDate)
    setOpen(false)
  }

  const handleSelectDate = (changedDate: Date | undefined) => {
    if (autoApply && changedDate) {
      onDateChange?.(changedDate)
      setOpen(false)
      return
    }

    setDate(changedDate)
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        {cloneElement(triggerButton, {
          onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation()
            setOpen((prev) => !prev)
          },
        })}
      </PopoverTrigger>

      <PopoverContent
        container={container}
        // max-sm override needed: the shared PopoverContent sets its own
        // max-sm:z-[110], which — being a later, same-specificity rule —
        // would otherwise beat this z-[9999] on mobile regardless of
        // magnitude, dropping this calendar behind dialogs that nest it
        // (e.g. CustomRecurrenceDialog, z-[1100])
        className="~w-auto ~p-0 ~overflow-hidden ~z-[9999] max-sm:~z-[9999]"
      >
        <Calendar
          classNames={{
            caption_label: '~text-base ~font-medium',
            nav_button: cn(
              ButtonVariants.base({ variant: 'outline' }),
              '~h-7 ~w-7 ~border-0 ~bg-transparent ~p-0 ~opacity-50 hover:~opacity-100 hover:~bg-transparent'
            ),
            head_cell:
              '~text-medium-grey ~rounded-3xs ~w-[36px] ~font-normal ~text-sm ~text-center ~uppercase',
            row: '~flex ~w-full ~mt-0',
            day_selected:
              '~bg-sunrise-30 ~text-black hover:~bg-sunrise-30 hover:~text-black focus:~bg-sunrise-30 focus:~text-black',
          }}
          components={{
            IconLeft: () => <ChevronLeft className="~h-7 ~w-7" />,
            IconRight: () => <ChevronRight className="~h-7 ~w-7" />,
          }}
          {...calendarProps}
          mode="single"
          selected={date || selectedDate}
          onSelect={handleSelectDate}
          disabled={disabled}
          month={month}
          onMonthChange={setMonth}
        />

        {footerContent ? (
          footerContent(handleSelectDate)
        ) : (
          <div className="~border-t ~border-t-grey-wash ~flex ~justify-between ~gap-2 ~p-4">
            <Button
              variant="link"
              size="sm"
              className="~p-0 ~text-sm ~font-medium ~text-link ~rounded-sm ~bg-transparent ~h-[36px] hover:~no-underline"
              onClick={() => {
                setDate(undefined)
                handleApplyDate(new Date())
              }}
            >
              Today
            </Button>

            <div className="~flex ~gap-2">
              <Button
                className="~px-2 ~text-medium-grey ~font-medium ~text-sm ~rounded-sm ~h-[36px] hover:~bg-transparent hover:~text-black"
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (!isEqual(date, selectedDate)) {
                    setDate(undefined)
                  }

                  setOpen(false)
                }}
              >
                Cancel
              </Button>

              <Button
                size="sm"
                className="~bg-navy ~text-white ~font-medium ~text-sm hover:~bg-navy ~rounded-lg ~px-6 ~h-[36px] ~w-auto"
                onClick={() => handleApplyDate(date)}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default memo(DatePicker)
